import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtAUD } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { FadeUp } from "@/components/Reveal";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ShieldCheck, ShieldOff, Lock, Radio } from "lucide-react";
import { toast } from "sonner";
import TierScreenText from "@/components/TierScreenText";

const TIER_META = {
  degoogled: { id: "01", label: "Degoogled", color: "#EAEAEA", Icon: ShieldOff,
    tagline: "Clean install of GrapheneOS. Google Play Services excised.",
    features: ["No Google Play Services", "F-Droid preinstalled", "Encrypted DNS", "Signal messenger", "Verified boot", "Sandboxed apps"] },
  secure: { id: "02", label: "Secure", color: "#EAEAEA", Icon: Lock,
    tagline: "Hardened memory. Traffic routed. Nothing leaks.",
    features: ["Everything from Degoogled", "Hardened memory allocator", "Always-on Mullvad VPN", "Sensor block in background", "Molly (hardened Signal)", "Aegis 2FA + OnionShare", "Duress PIN wipe"] },
  phantom: { id: "03", label: "Phantom", color: "#7C3AED", Icon: Radio,
    tagline: "Physical mic disconnect. USB severed. Silence weaponised.",
    features: ["Everything from Secure", "Physical microphone removal", "USB data pin severed", "Per-app user profiles", "Location hardware-blocked", "Custom locked bootloader", "Encrypted eSIM slots", "Faraday case included"] },
};

const TIERS = ["degoogled", "secure", "phantom"];
const STORAGES = ["256GB", "512GB", "1TB"];

export default function Product() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [tier, setTier] = useState("degoogled");
  const [storage, setStorage] = useState("256GB");
  const [activeImg, setActiveImg] = useState(0);
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(null));
    setActiveImg(0);
  }, [slug]);

  const variant = useMemo(() => {
    if (!p?.variants) return null;
    return p.variants.find((v) => v.tier === tier && v.storage === storage) || null;
  }, [p, tier, storage]);

  if (!p) return <div className="pt-40 px-6 md:px-10 min-h-[70svh]"><div className="text-[#888]">Loading…</div></div>;

  const meta = TIER_META[tier];

  const addProduct = () => ({
    lookup_key: variant.lookup_key,
    slug: p.slug,
    name: `${p.name} · ${TIER_META[tier].label} · ${storage}`,
    price_aud: variant.price_aud,
    tier, storage,
    image_url: (p.image_urls || [p.image_url])[0],
  });

  const handleAdd = () => {
    add(addProduct(), 1);
    toast.success("Added to vault cart", { description: `${p.name} · ${meta.label} · ${storage}` });
  };
  const handleBuy = () => {
    add(addProduct(), 1);
    nav("/cart");
  };

  return (
    <div className="pt-28 pb-24" data-testid={`product-detail-${p.slug}`}>
      <div className="px-6 md:px-10">
        <Link to="/shop" className="btn-ghost inline-flex" data-testid="back-to-shop">
          <ArrowLeft size={14} /> Back to catalogue
        </Link>
      </div>
      <div className="mt-8 grid grid-cols-12 gap-0 border-t border-[#1A1A1A]">
        <div className="col-span-12 md:col-span-7 relative border-r-0 md:border-r border-[#1A1A1A] bg-[#080808]">
          <div className="relative min-h-[60svh] md:min-h-[100svh] overflow-hidden" data-testid="product-gallery">
            {(() => {
              const gallery = (p.image_urls && p.image_urls.length > 0) ? p.image_urls : [p.image_url];
              const currentSrc = gallery[activeImg] || gallery[0];
              const isFrontOrOpen = /_front|_open/.test(currentSrc);
              return (
                <>
                  <img key={currentSrc} src={currentSrc} alt={p.name} className={`absolute inset-0 w-full h-full object-cover img-clip ${tier === "phantom" ? "phantom" : ""}`} />
                  {isFrontOrOpen && <TierScreenText tier={tier} size="lg" />}
                  <div className="absolute top-6 left-6 flex items-center gap-3 z-30">
                    <span className={`tier-dot ${tier}`} />
                    <span className="overline" style={{ color: meta.color }}>{tier}</span>
                  </div>
                  {gallery.length > 1 && (
                    <div className="absolute bottom-16 left-6 right-6 flex gap-3 z-30">
                      {gallery.map((src, i) => (
                        <button key={src + i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                          className={`w-16 h-16 md:w-20 md:h-20 border transition-all overflow-hidden ${activeImg === i ? "border-[#EAEAEA] opacity-100" : "border-[#1A1A1A] opacity-60 hover:opacity-100"}`}>
                          <img src={src} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between font-mono text-[10px] uppercase tracking-widest text-[#666] z-30">
                    <span>PP · {p.slug.toUpperCase()}</span>
                    <span>SN · ██████</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div className="col-span-12 md:col-span-5 p-8 md:p-12 flex flex-col justify-between">
          <div>
            <div className="overline">{p.model}</div>
            <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] mt-4">
              {p.name}
              <br />
              <span style={{ color: meta.color }}>//&nbsp;{meta.label}</span>
            </h1>
            <p className="mt-8 text-[#EAEAEA] text-base leading-relaxed">{meta.tagline}</p>
            <p className="mt-6 text-[#888] text-sm leading-relaxed">{p.long_description}</p>

            {/* Tier selector */}
            <div className="mt-10" data-testid="tier-selector">
              <div className="overline mb-4">Hardening tier</div>
              <div className="grid grid-cols-3 gap-2">
                {TIERS.map((t) => {
                  const m = TIER_META[t];
                  const Icon = m.Icon;
                  const active = tier === t;
                  const priceAtTier = p.variants?.find((v) => v.tier === t && v.storage === storage)?.price_aud || 0;
                  return (
                    <button key={t} onClick={() => setTier(t)} data-testid={`tier-${t}`}
                      className={`border p-4 text-left transition-colors ${active ? (t === "phantom" ? "border-[#7C3AED] bg-[#0A0A0A]" : "border-[#EAEAEA] bg-[#0A0A0A]") : "border-[#1A1A1A] hover:border-[#333]"}`}>
                      <div className="flex items-center justify-between">
                        <Icon size={16} style={{ color: m.color }} strokeWidth={1.4} />
                        <span className={`tier-dot ${t}`} />
                      </div>
                      <div className="font-display text-lg mt-3" style={{ color: active ? m.color : "#EAEAEA" }}>
                        {m.label}
                      </div>
                      <div className="overline text-[#888] mt-1">{fmtAUD(priceAtTier)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Storage selector */}
            <div className="mt-8" data-testid="storage-selector">
              <div className="overline mb-4">Storage</div>
              <div className="grid grid-cols-3 gap-2">
                {STORAGES.map((s) => {
                  const active = storage === s;
                  const priceAtSize = p.variants?.find((v) => v.tier === tier && v.storage === s)?.price_aud || 0;
                  return (
                    <button key={s} onClick={() => setStorage(s)} data-testid={`storage-${s}`}
                      className={`border p-4 text-left transition-colors ${active ? "border-[#EAEAEA] bg-[#0A0A0A]" : "border-[#1A1A1A] hover:border-[#333]"}`}>
                      <div className="font-display text-xl">{s}</div>
                      <div className="overline text-[#888] mt-1">{fmtAUD(priceAtSize)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-10">
              <div className="overline mb-4">{meta.label} — included</div>
              <ul className="space-y-3">
                {meta.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[#EAEAEA] text-sm">
                    <Check size={16} className="mt-0.5" style={{ color: meta.color }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <div className="overline mb-4">Specifications</div>
              <div className="grid grid-cols-2 gap-0 border border-[#1A1A1A]">
                {Object.entries(p.specs || {}).map(([k, v], i) => (
                  <FadeUp key={k} delay={i * 0.04}>
                    <div className="p-4 border-r border-b border-[#1A1A1A] last:border-r-0">
                      <div className="overline text-[#444]">{k}</div>
                      <div className="text-[#EAEAEA] font-mono text-sm mt-2">{v}</div>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>

            {tier === "phantom" && (
              <div className="mt-8 border border-[#7C3AED]/40 p-5">
                <div className="overline text-[#7C3AED]">Deep-dive</div>
                <div className="font-display text-xl mt-2">The Phantom Hardening protocol.</div>
                <p className="text-[#888] text-sm mt-3">Kernel-level lockdown, proprietary blob removal, encrypted eSIMs, hand-signed audit trail.</p>
                <Link to="/hardening" className="btn-ghost mt-4 inline-flex" data-testid="phantom-hardening-link">Read the audit spec →</Link>
              </div>
            )}
          </div>

          <div className="mt-12 border-t border-[#1A1A1A] pt-8">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="overline">Price</div>
                <div className="text-[#666] text-xs mt-1">{meta.label} · {storage}</div>
              </div>
              <div>
                <span className="font-display text-4xl">{fmtAUD(variant?.price_aud || p.base_price_aud || 0)}</span>
                <span className="overline ml-3">AUD · inc. hardening</span>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <button className={`btn-vault flex-1 justify-center ${tier === "phantom" ? "danger" : ""}`} onClick={handleBuy} data-testid="buy-now-btn">Buy now</button>
              <button className="btn-vault flex-1 justify-center" onClick={handleAdd} data-testid="add-to-cart-btn">Add to cart</button>
            </div>
            <div className="mt-6 flex items-center gap-2 text-[#444] text-xs uppercase tracking-widest">
              <ShieldCheck size={14} /> Hand hardened · tamper-evident shipping · 5–7 business days
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
